import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Badge } from '../ui/Badge';
import { SkeletonList } from '../ui/Skeleton';
import i18n from '../../i18n';
import { formatEUR } from '../../lib/format';
import { DashboardPanel, PanelEmpty } from './DashboardPanel';

export interface StockAlertRow {
  id: string;
  product_id: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  current_stock: number;
  threshold: number;
  product_name?: string | null;
  product_ref?: string | null;
  product_price?: string | number | null;
}

/**
 * Priority → badge. A CRITICAL alert on a part that is actually at zero is a stock-out
 * ("Out of stock"), which is a different call to action than "reorder soon", so the label is
 * driven by the stock level and not by priority alone.
 */
function alertBadge(r: StockAlertRow) {
  if (Number(r.current_stock) <= 0)
    return { label: i18n.t('dashboard.outOfStock'), variant: 'destructive' } as const;
  if (r.priority === 'CRITICAL' || r.priority === 'HIGH')
    return { label: i18n.t('dashboard.urgent'), variant: 'warning' } as const;
  return { label: i18n.t('dashboard.normal'), variant: 'muted' } as const;
}

/** Dashboard panel — active reorder alerts, priority-sorted by the backend. */
export function StockAlertsPanel({
  rows,
  loading,
  limit = 4,
}: {
  rows: StockAlertRow[] | null;
  loading?: boolean;
  limit?: number;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const visible = (rows ?? []).slice(0, limit);

  return (
    <DashboardPanel title={t('dashboard.stockAlerts')} viewAllHref="/stock/alerts">
      {loading ? (
        <div className="p-5">
          <SkeletonList rows={limit} />
        </div>
      ) : visible.length === 0 ? (
        <PanelEmpty>{t('dashboard.noActiveAlerts')}</PanelEmpty>
      ) : (
        <ul>
          {visible.map((r) => {
            const badge = alertBadge(r);
            return (
              <li
                key={r.id}
                onClick={() => navigate('/stock/alerts')}
                className="border-t first:border-t-0 px-5 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/50"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {r.product_name ?? r.product_id.slice(0, 8)}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {r.product_ref ?? '—'}
                    {r.product_price != null && ` · ${formatEUR(r.product_price)}`}
                  </div>
                </div>
                <Badge variant={badge.variant} className="shrink-0 uppercase">
                  {badge.label}
                </Badge>
                {/* Stock against the reorder threshold that raised the alert. */}
                <div className="text-xs tabular-nums text-muted-foreground shrink-0 w-10 text-right">
                  {r.current_stock}/{r.threshold}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </DashboardPanel>
  );
}
